import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { assignmentService, type AssignmentWithDetails } from '../../services'
import { useAuthStore } from '../../stores'
import { useToast } from '../common/Toast'
import { EmailStatusBadge, ResendEmailButton } from '../assignment'

interface ExistingAssignmentsListProps {
  programId: string
  providerId: string
  maxVisible?: number
  onAssignedClientsLoaded?: (clientIds: string[]) => void
}

type StatusGroup = 'active' | 'completed' | 'expired'

function getStatusGroup(status: string, expiresAt: string | null): StatusGroup {
  if (status === 'completed') return 'completed'
  if (expiresAt && new Date(expiresAt) < new Date()) return 'expired'
  return 'active'
}

function getStatusBadge(statusGroup: StatusGroup) {
  switch (statusGroup) {
    case 'active':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Active
        </span>
      )
    case 'completed':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          Completed
        </span>
      )
    case 'expired':
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          Expired
        </span>
      )
  }
}

export function ExistingAssignmentsList({
  programId,
  providerId,
  maxVisible = 5,
  onAssignedClientsLoaded,
}: ExistingAssignmentsListProps) {
  const { user } = useAuthStore()
  const toast = useToast()
  const [assignments, setAssignments] = useState<AssignmentWithDetails[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const loadAssignments = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await assignmentService.getAssignments(providerId, {
          programId,
        })
        setAssignments(data)
        // Notify parent of assigned client IDs
        if (onAssignedClientsLoaded) {
          const clientIds = data.map((a) => a.client_id)
          onAssignedClientsLoaded(clientIds)
        }
      } catch (err) {
        console.error('Failed to load assignments:', err)
        setError('Failed to load assignments')
      } finally {
        setIsLoading(false)
      }
    }

    loadAssignments()
  }, [programId, providerId, onAssignedClientsLoaded])

  const reloadAssignments = async () => {
    try {
      const data = await assignmentService.getAssignments(providerId, {
        programId,
      })
      setAssignments(data)
    } catch (err) {
      console.error('Failed to reload assignments:', err)
    }
  }

  const handleResendEmail = async (assignment: AssignmentWithDetails) => {
    if (!assignment.client?.email) {
      toast.error('Client has no email address')
      return
    }
    try {
      const result = await assignmentService.sendAssignmentEmail(
        {
          ...assignment,
          client: {
            id: assignment.client_id,
            email: assignment.client.email,
            full_name: assignment.client.full_name,
          },
        } as any,
        assignment.program?.title || 'Exercise Program',
        user?.user_metadata?.full_name || 'Your Coach',
        0 // Exercise count not available in list context
      )
      if (result.success) {
        toast.success('Email sent successfully')
        reloadAssignments()
      } else {
        toast.error(result.error || 'Failed to send email')
      }
    } catch {
      toast.error('Failed to send email')
    }
  }

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-4 bg-gray-200 rounded w-1/3"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
        <div className="h-12 bg-gray-200 rounded"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-sm text-red-600 p-4 bg-red-50 rounded-lg">
        {error}
      </div>
    )
  }

  if (assignments.length === 0) {
    return (
      <div className="text-center py-6 bg-gray-50 rounded-lg">
        <svg
          className="mx-auto h-10 w-10 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
        <p className="mt-2 text-sm text-gray-500">
          No clients assigned to this program yet
        </p>
      </div>
    )
  }

  // Calculate status counts
  const statusCounts = assignments.reduce(
    (acc, a) => {
      const group = getStatusGroup(a.status, a.expires_at)
      acc[group]++
      return acc
    },
    { active: 0, completed: 0, expired: 0 } as Record<StatusGroup, number>
  )

  const visibleAssignments = assignments.slice(0, maxVisible)
  const remainingCount = assignments.length - maxVisible

  return (
    <div className="space-y-4">
      {/* Status Summary */}
      <div className="flex items-center gap-4 text-sm">
        <span className="text-gray-600 font-medium">
          {assignments.length} Assignment{assignments.length !== 1 ? 's' : ''}:
        </span>
        {statusCounts.active > 0 && (
          <span className="text-green-600">
            {statusCounts.active} active
          </span>
        )}
        {statusCounts.completed > 0 && (
          <span className="text-blue-600">
            {statusCounts.completed} completed
          </span>
        )}
        {statusCounts.expired > 0 && (
          <span className="text-gray-500">
            {statusCounts.expired} expired
          </span>
        )}
      </div>

      {/* Assignment List */}
      <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
        {visibleAssignments.map((assignment) => {
          const statusGroup = getStatusGroup(assignment.status, assignment.expires_at)
          const progress = assignment.progress_summary?.progress_percent || 0
          const isActive = statusGroup === 'active'

          return (
            <div
              key={assignment.id}
              className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
            >
              <Link
                to={`/dashboard/assignments/${assignment.id}`}
                className="flex items-center gap-3 min-w-0 flex-1"
              >
                {/* Avatar placeholder */}
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-medium text-gray-600">
                    {assignment.client.full_name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>

                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {assignment.client.full_name || assignment.client.email}
                  </p>
                  {progress > 0 && (
                    <div className="flex items-center gap-2 mt-0.5">
                      <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500">{Math.round(progress)}%</span>
                    </div>
                  )}
                </div>
              </Link>

              <div className="flex items-center gap-2 flex-shrink-0">
                {/* Email status & resend */}
                <EmailStatusBadge
                  emailSentAt={assignment.email_sent_at}
                  emailSendCount={assignment.email_send_count || 0}
                  emailLastError={assignment.email_last_error}
                />
                {isActive && (
                  <ResendEmailButton
                    emailSentAt={assignment.email_sent_at}
                    clientEmail={assignment.client?.email || null}
                    onResend={() => handleResendEmail(assignment)}
                    compact
                  />
                )}
                {getStatusBadge(statusGroup)}
                <Link
                  to={`/dashboard/assignments/${assignment.id}`}
                  className="p-1 text-gray-400 hover:text-gray-600"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </div>
          )
        })}
      </div>

      {/* View All Link */}
      {remainingCount > 0 && (
        <Link
          to={`/dashboard/assignments?program=${programId}`}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          View all {assignments.length} assignments
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
          </svg>
        </Link>
      )}
    </div>
  )
}
